import { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import logError from "commons/utils/logError";
import PushDeviceService from "services/notification/PushDeviceService";

function hasPushSubscriptionId() {
  const id = window.__BEEDOO_ONESIGNAL_ID__;
  return typeof id === "string" && id.length > 0;
}

function PushFromNativeBridge() {
  const history = useHistory();

  const openChannelFromPush = useCallback(
    (channelId) => {
      if (channelId == null || channelId === "") {
        return;
      }
      history.push(`/channel/${encodeURIComponent(String(channelId))}`);
    },
    [history]
  );

  const openHomeFromPush = useCallback(() => {
    history.push("/home");
  }, [history]);

  const openFeedFromPush = useCallback(
    (postId) => {
      if (postId == null || postId === "") {
        return;
      }
      history.push(`/feed/${encodeURIComponent(String(postId))}`);
    },
    [history]
  );

  const openWikiFromPush = useCallback(
    (articleId) => {
      if (articleId == null || articleId === "") {
        return;
      }
      history.push(
        `/wiki/${encodeURIComponent(String(articleId))}/w`
      );
    },
    [history]
  );

  const openQuizFromPush = useCallback(
    (courseId, classId) => {
      if (
        courseId == null ||
        courseId === "" ||
        classId == null ||
        classId === ""
      ) {
        return;
      }
      history.push(
        `/quiz/${encodeURIComponent(String(courseId))}/${encodeURIComponent(String(classId))}`
      );
    },
    [history]
  );

  const openLightningTestFromPush = useCallback(
    (testId) => {
      if (testId == null || testId === "") {
        return;
      }
      history.push(`/quizpop/${encodeURIComponent(String(testId))}`);
    },
    [history]
  );

  useEffect(() => {
    window.openChannelFromPush = openChannelFromPush;
    window.openHomeFromPush = openHomeFromPush;
    window.openFeedFromPush = openFeedFromPush;
    window.openWikiFromPush = openWikiFromPush;
    window.openQuizFromPush = openQuizFromPush;
    window.openLightningTestFromPush = openLightningTestFromPush;
    return () => {
      delete window.openChannelFromPush;
      delete window.openHomeFromPush;
      delete window.openFeedFromPush;
      delete window.openWikiFromPush;
      delete window.openQuizFromPush;
      delete window.openLightningTestFromPush;
    };
  }, [openChannelFromPush, openHomeFromPush, openFeedFromPush, openWikiFromPush, openQuizFromPush, openLightningTestFromPush]);

  useEffect(() => {
    const tryRegister = () => {
      if (!hasPushSubscriptionId()) {
        return;
      }
      const id = window.__BEEDOO_ONESIGNAL_ID__;
      const platform = window.__BEEDOO_ONESIGNAL_PLATFORM__ || "unknown";
      PushDeviceService.register(id, platform).catch((err) =>
        logError("PushFromNativeBridge.register", err)
      );
    };

    tryRegister();

    window.addEventListener("beedoo.onesignal", tryRegister);
    return () => {
      window.removeEventListener("beedoo.onesignal", tryRegister);
    };
  }, []);

  useEffect(() => {
    const syncHeartbeat = () => {
      if (!hasPushSubscriptionId()) {
        PushDeviceService.stopHeartbeat();
        return;
      }
      if (document.visibilityState === "visible") {
        PushDeviceService.startHeartbeat();
      } else {
        PushDeviceService.stopHeartbeat();
      }
    };

    syncHeartbeat();

    document.addEventListener("visibilitychange", syncHeartbeat);
    window.addEventListener("beedoo.onesignal", syncHeartbeat);
    return () => {
      document.removeEventListener("visibilitychange", syncHeartbeat);
      window.removeEventListener("beedoo.onesignal", syncHeartbeat);
      PushDeviceService.stopHeartbeat();
    };
  }, []);

  return null;
}

PushFromNativeBridge.propTypes = {};

export default PushFromNativeBridge;
